import { useMemo } from "react";
import ProgressTracker, { ProgressStep } from "@/components/ui/ProgressTracker";
import { BookingStatus } from "@/types/activities";

interface BookingWorkflowProgressProps {
  currentStatus: BookingStatus;
  className?: string;
  orientation?: "horizontal" | "vertical";
  showDescriptions?: boolean;
}

const workflowSteps: Array<{
  id: string;
  status: BookingStatus;
  title: string;
  description: string;
  tooltip?: string;
}> = [
  {
    id: "vendor-contact",
    status: BookingStatus.PendingVendorContact,
    title: "Vendor Contact",
    description: "Contacting vendors for availability",
    tooltip: "Manual: Team starts working on booking"
  },
  {
    id: "vendor-negotiating",
    status: BookingStatus.VendorNegotiating,
    title: "Negotiating",
    description: "Vendor responding with proposals",
    tooltip: "Team creates booking options and contacts vendors offline"
  },
  {
    id: "org-approval",
    status: BookingStatus.PendingOrganizationApproval,
    title: "Organization Approval",
    description: "Awaiting organization response",
    tooltip: "Auto: Triggered when booking option is created. Organization will approve/reject/counter."
  },
  {
    id: "vendor-booking",
    status: BookingStatus.ApprovedAwaitingVendorBooking,
    title: "Vendor Booking",
    description: "Finalizing booking with vendor",
    tooltip: "Auto: Triggered when organization approves. Team confirms booking with vendor."
  },
  {
    id: "confirmed",
    status: BookingStatus.Confirmed,
    title: "Confirmed",
    description: "Booking confirmed and scheduled",
    tooltip: "Auto: Triggered when team confirms vendor booking"
  }
];

const getStepStatus = (
  stepStatus: BookingStatus,
  currentStatus: BookingStatus,
  statusOrder: BookingStatus[]
): ProgressStep["status"] => {
  const currentIndex = statusOrder.indexOf(currentStatus);
  const stepIndex = statusOrder.indexOf(stepStatus);

  // Handle special cases for terminal states
  if (currentStatus === "Cancelled") {
    return stepIndex <= currentIndex ? "error" : "pending";
  }

  if (currentStatus === "NoVendorsAvailable") {
    return stepIndex === 0 ? "error" : "skipped";
  }

  // Normal workflow progression
  if (stepIndex < currentIndex) {
    return "completed";
  } else if (stepIndex === currentIndex) {
    return "current";
  } else {
    return "pending";
  }
};

export default function BookingWorkflowProgress({
  currentStatus,
  className,
  orientation = "horizontal",
  showDescriptions = false
}: BookingWorkflowProgressProps) {
  const statusOrder: BookingStatus[] = [
    BookingStatus.PendingVendorContact,
    BookingStatus.VendorNegotiating,
    BookingStatus.PendingOrganizationApproval,
    BookingStatus.ApprovedAwaitingVendorBooking,
    BookingStatus.Confirmed
  ];

  const progressSteps = useMemo(() => {
    const steps: ProgressStep[] = workflowSteps.map((step) => ({
      id: step.id,
      title: step.title,
      description: step.description,
      status: getStepStatus(step.status, currentStatus, statusOrder)
    }));

    // Add cancelled step if booking is cancelled
    if (currentStatus === "Cancelled") {
      steps.push({
        id: "cancelled",
        title: "Cancelled",
        description: "Booking was cancelled",
        status: "current"
      });
    }

    // Add no vendors step if no vendors available
    if (currentStatus === "NoVendorsAvailable") {
      steps[0] = {
        ...steps[0],
        status: "error"
      };
      steps.push({
        id: "no-vendors",
        title: "No Vendors",
        description: "No vendors available",
        status: "current"
      });
    }

    return steps;
  }, [currentStatus, statusOrder]);

  return (
    <ProgressTracker
      steps={progressSteps}
      className={className}
      orientation={orientation}
      showDescriptions={showDescriptions}
    />
  );
}

// Mini version for compact spaces
interface MiniWorkflowProgressProps {
  currentStatus: BookingStatus;
  className?: string;
}

export function MiniWorkflowProgress({ currentStatus, className }: MiniWorkflowProgressProps) {
  const statusOrder: BookingStatus[] = [
    BookingStatus.PendingVendorContact,
    BookingStatus.VendorNegotiating,
    BookingStatus.PendingOrganizationApproval,
    BookingStatus.ApprovedAwaitingVendorBooking,
    BookingStatus.Confirmed
  ];

  const currentIndex = statusOrder.indexOf(currentStatus);
  const totalSteps = statusOrder.length;

  const getProgressPercentage = () => {
    if (currentStatus === "Cancelled" || currentStatus === "NoVendorsAvailable") {
      return 0;
    }
    if (currentIndex === -1) return 0;
    return ((currentIndex + 1) / totalSteps) * 100;
  };

  const progressPercentage = getProgressPercentage();

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${
            currentStatus === "Cancelled" || currentStatus === "NoVendorsAvailable"
              ? "bg-red-500"
              : currentStatus === "Confirmed"
              ? "bg-green-500"
              : "bg-blue-500"
          }`}
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {currentStatus === "Cancelled" && "Cancelled"}
        {currentStatus === "NoVendorsAvailable" && "No Vendors"}
        {currentStatus === "Confirmed" && "Completed"}
        {!["Cancelled", "NoVendorsAvailable", "Confirmed"].includes(currentStatus) &&
          `${currentIndex + 1}/${totalSteps}`
        }
      </span>
    </div>
  );
}